import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {dateFormatter, fromGenericBeToGenericFe, useActionColDef,} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {useRecoilState} from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import {Box, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import DialogComponent from "../../common/components/DialogComponent";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {ColDef, ICellRendererParams, ValueFormatterParams,} from "ag-grid-community";
import dayjs from "dayjs";
import Loading from "../../common/components/Loading";
import OWAncillaryFilters from "./Filters/OWAncillaryFilters";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE,} from "../../api/types/generic";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import ActionsPopover from "../../common/components/ActionsPopover";
import BasicToolbar from "../../common/components/BasicToolbar";
import {useCommercialSegmentRepository} from "../../api/repositories/CommercialSegmentRepository";
import {commercialSegmentListAtom} from "../../state/commercialSegmentsAtom";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import {useOWAncillaryRepository} from "../../api/repositories/OWAncillaryRepository";
import {FormSearchOWAncillaryModel, OWAncillaryEntityResponse,} from "../../api/types/owAncillary";
import OWAncillaryContainerView from "../OWAncillary/OWAncillaryContainerView";

const OWAncillaries = () => {
    // Use Translation
    const {t, i18n} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [, setCommercialSegmentList] = useRecoilState<SelectElementModelFE[]>(
        commercialSegmentListAtom
    );

    // Use Repository Hooks
    const {
        getOWAncillariesPaged,
        deleteOWAncillary,
        terminateOWAncillary
    } = useOWAncillaryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // Use State
    const [viewOWAncillary, setViewOWAncillary] = useState<boolean>(false);
    const [rowIndex, setRowIndex] = useState<number | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchOWAncillaryModel>({
        ancillaryCode: "",
        commercialSegmentId: null,
        pickupDateFrom: "",
        pickupDateTo: "",
        reservationFrom: "",
        reservationTo: "",
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
        // eslint-disable-next-line
    }, []);

    /* AG GRID - START */
    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<OWAncillaryEntityResponse, FormSearchOWAncillaryModel>({
            gridRef,
            api: getOWAncillariesPaged,
            initialSortColId: "prcAncillary.nameAncillary",
            entityFilters: testFilters,
            customSort: "OWAncillaryEntityResponse",
        });

    useEffect(() => {
        if (gridRef?.current) {
            handleTriggerFilters({
                ancillaryCode: testFilters.ancillaryCode,
                commercialSegmentId: (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code as number,
                reservationFrom: testFilters.reservationFrom
                    ? dayjs(testFilters.reservationFrom).toISOString()
                    : testFilters.reservationFrom,
                reservationTo: testFilters.reservationTo
                    ? dayjs(testFilters.reservationTo).toISOString()
                    : testFilters.reservationTo,
                pickupDateFrom: testFilters.pickupDateFrom
                    ? dayjs(testFilters.pickupDateFrom).toISOString()
                    : testFilters.pickupDateFrom,
                pickupDateTo: testFilters.pickupDateTo
                    ? dayjs(testFilters.pickupDateTo).toISOString()
                    : testFilters.pickupDateTo,
                legalEntities:
                    ((testFilters.legalEntities as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? everyLegalEntitiesWithoutAll
                        : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
                dealers:
                    ((testFilters.dealers as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? dealerFilteredOptions
                        : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
            });
        }
    }, [testFilters.showOnlyActive]);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            ancillaryCode: testFilters.ancillaryCode,
            commercialSegmentId: (
                testFilters.commercialSegmentId as SelectElementModelFE
            )?.code as number,
            reservationFrom: testFilters.reservationFrom
                ? dayjs(testFilters.reservationFrom).toISOString()
                : testFilters.reservationFrom,
            reservationTo: testFilters.reservationTo
                ? dayjs(testFilters.reservationTo).toISOString()
                : testFilters.reservationTo,
            pickupDateFrom: testFilters.pickupDateFrom
                ? dayjs(testFilters.pickupDateFrom).toISOString()
                : testFilters.pickupDateFrom,
            pickupDateTo: testFilters.pickupDateTo
                ? dayjs(testFilters.pickupDateTo).toISOString()
                : testFilters.pickupDateTo,
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
            dealers:
                ((testFilters.dealers as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? dealerFilteredOptions
                    : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleTriggerResetFilters = () => {
        setTestFilters({
            ancillaryCode: "",
            commercialSegmentId: null,
            pickupDateFrom: "",
            pickupDateTo: "",
            reservationFrom: "",
            reservationTo: "",
            dealers: [],
            legalEntities: []
        });
        handleTriggerFilters({
            ancillaryCode: "",
            commercialSegmentId: null,
            pickupDateFrom: "",
            pickupDateTo: "",
            reservationFrom: "",
            reservationTo: "",
            dealers: [],
            legalEntities: []
        });
    };

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef<OWAncillaryEntityResponse>[] = [
        {
            field: "legalEntities",
            headerName: t("pricing:ancillary.form.labels.legalEntity"),
            headerTooltip: t("pricing:ancillary.form.labels.legalEntity"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.prcAncillary.legalEntity)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:ancillary.form.labels.dealer"),
            headerTooltip: t("pricing:ancillary.form.labels.dealer"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.prcAncillary.dealer)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "prcAncillary.nameAncillary",
            headerName: t("pricing:ancillary.form.labels.nameAncillary"),
            headerTooltip: t("pricing:ancillary.form.labels.nameAncillary"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams<OWAncillaryEntityResponse>) => {
                // LPP-21049: Skip defaultLabel fallback to avoid showing description instead of name
                const multilanguage = params.data?.prcAncillary?.prcMultilanguage;
                if (multilanguage && multilanguage[i18n.language]) {
                    return multilanguage[i18n.language];
                }
                return params.data?.prcAncillary.nameAncillary;
            }
        },
        {
            field: "ancillaryCode",
            headerName: t("pricing:ancillary.form.labels.code"),
            headerTooltip: t("pricing:ancillary.form.labels.code"),
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams<OWAncillaryEntityResponse>) => params.data?.prcAncillary.code
        },
        {
            field: "commercialSegmentId",
            headerName: t("pricing:ancillary.form.labels.commercialSegment"),
            headerTooltip: t("pricing:ancillary.form.labels.commercialSegment"),
            flex: 1,
            sortable: false,
            cellRenderer: (params: ICellRendererParams<OWAncillaryEntityResponse>) => params.data?.prcAncillary.segments.map((el: SelectElementModelBE) => el.name).join(", "),
            filter: "text"
        },
        {
            field: "reservationFrom",
            headerName: t("pricing:ancillary.form.labels.reservationFrom"),
            headerTooltip: t("pricing:ancillary.form.labels.reservationFrom"),
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<OWAncillaryEntityResponse>) => params.data?.prcAncillary.reservationFrom ? dateFormatter(params.data?.prcAncillary.reservationFrom) : "",
            filter: "text"
        },
        {
            field: "reservationTo",
            headerName: t("pricing:ancillary.form.labels.reservationTo"),
            headerTooltip: t("pricing:ancillary.form.labels.reservationTo"),
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<OWAncillaryEntityResponse>) => params.data?.prcAncillary.reservationTo ? dateFormatter(params.data?.prcAncillary.reservationTo) : "",
            filter: "text"
        },
        {
            field: "pickupDateFrom",
            headerName: t("pricing:ancillary.form.labels.pickupDateFrom"),
            headerTooltip: t("pricing:ancillary.form.labels.pickupDateFrom"),
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<OWAncillaryEntityResponse>) => params.data?.prcAncillary.pickupFrom ? dateFormatter(params.data?.prcAncillary.pickupFrom) : "",
            filter: "text"
        },
        {
            field: "pickupDateTo",
            headerName: t("pricing:ancillary.form.labels.pickupDateTo"),
            headerTooltip: t("pricing:ancillary.form.labels.pickupDateTo"),
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<OWAncillaryEntityResponse>) => params.data?.prcAncillary.pickupTo ? dateFormatter(params.data?.prcAncillary.pickupTo) : "",
            filter: "text"
        },
        {
            field: "ancillaryType",
            hide: true,
            filter: "text"
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    url={`../ow/ow-ancillary/create/${params.data.id}`}
                    params={params}
                    view={{
                        action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_OW_ANCILLARY_CONTAINER"
                            })
                        })
                    }}
                    update={{
                        action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_OW_ANCILLARY_CONTAINER"
                            })
                        })
                    }}
                    terminate={{
                        action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_TERMINATE_OW_ANCILLARY_CONTAINER"
                            })
                        })
                    }}
                    del={!params.data.prcAncillary.used ? {
                        action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_OW_ANCILLARY_CONTAINER"
                            })
                        })
                    } : undefined}
                />
            )
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;
    /* AG GRID - END */

    /* TABLE ACTIONS - START */
    const viewButtonClicked = (data: OWAncillaryEntityResponse) => {
        setViewOWAncillary(true);
        setRowIndex(data.id);
    };

    const updateButtonClicked = (data: OWAncillaryEntityResponse) => {
        //navigate("../ow/ow-ancillary/create", {replace: true, state: {owAncillaryId: data.id}});
    };

    const terminate = async (id: number) => {
        await terminateOWAncillary(id);
        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const terminateButtonClicked = async (data: OWAncillaryEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => terminate(data.id),
            message: t("pricing:ancillariesOW.dialogContents.terminateAncillaryInfo"),
        });
    };

    const deleteButtonClicked = (data: OWAncillaryEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:ancillariesOW.dialogContents.removeAncillaryInfo"),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteOWAncillary(id);
                setDialog(null);
                await refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };
    /* TABLE ACTIONS - END */

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_OW_ANCILLARY_CONTAINER";
                        });
                    }) ?
                        {
                            onClick: () => {
                            }
                        } : undefined}
                url={"../ow/ow-ancillary/create/new"}
            />
        )
    };

    return (
        <Box>
            <SectionHeader
                title={t(
                    "translation:auths.SCOPE_PRC_VIEW_OW_ANCILLARY_CONTAINER.name"
                )}
            />
            <OWAncillaryFilters
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                    {viewOWAncillary && rowIndex && (
                        <OWAncillaryContainerView
                            owAncillaryId={rowIndex}
                            open={viewOWAncillary}
                            setOpen={setViewOWAncillary}
                        />
                    )}
                    {dialog?.open && (
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    )}
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default OWAncillaries;
